import { AggregateNullValueType } from "~/enums/AggregateNullValueType";
import { AggregateType } from "~/enums/aggregateType";
import { FileAttachmentType } from "~/enums/fileAttachmentType";
import { PickListSelectionType } from "~/enums/pickListSelectionType";
import { OptionRangeType } from "../../enums/optionRangeType";
import CharacterCountFactory from "../../../formability/form_builder/models/characterCounts/characterCountFactory";
import { CurrencyType } from "../../../formability/form_builder/models/currencyType";
import { InputFormatType } from "../../../formability/form_builder/models/inputFormatType";
import assertNever from "../../../global/assetNever";
import {
  ObjectModelPropertyChildType,
  ObjectModelPropertyRootType,
  ObjectModelPropertyType
} from "../../enums/objectModelPropertyType";
import { EnumHelper } from "../../helpers/enumHelper";
import { DatetimeMode } from "../datetimeMode";
import { DatetimeType } from "../datetimeType";
import { TimeFormat } from "../timeFormat";
import ObjectModelProperty from "./objectModelProperty";
import { ObjectModelPropertyCapability } from "./objectModelPropertyCapability";
import {IncrementalResetType} from "~/enums/incrementalResetType";

export default class ObjectModelPropertyCapabilityManager {
  static debug: boolean = false;

  static LABELABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Text,
    ObjectModelPropertyRootType.LongText,
    ObjectModelPropertyRootType.PickList,
    ObjectModelPropertyRootType.Number,
    ObjectModelPropertyRootType.DateTime,
    ObjectModelPropertyRootType.MappedProperty,
    ObjectModelPropertyRootType.RelationshipCount,
    ObjectModelPropertyRootType.DerivedField,
    ObjectModelPropertyRootType.AutoIncrement,
    ObjectModelPropertyRootType.FileAttachment,
    ObjectModelPropertyRootType.Aggregate,
  ];

  static REQUIREABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Text,
    ObjectModelPropertyRootType.LongText,
    ObjectModelPropertyRootType.PickList,
    ObjectModelPropertyRootType.Number,
    ObjectModelPropertyRootType.DateTime,
    ObjectModelPropertyRootType.FileAttachment,
  ];

  static OPTION_RANGE_REQUIREABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.PickList,
  ];

  static FORMATTABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Text,
    ObjectModelPropertyRootType.Number,
  ];

  static NUMBER_FORMATTABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Number,
  ];

  static LENGTHABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Text,
    ObjectModelPropertyRootType.LongText,
  ];

  static INDEXABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Text,
    ObjectModelPropertyRootType.PickList,
    ObjectModelPropertyRootType.Number,
    ObjectModelPropertyRootType.DateTime,
    ObjectModelPropertyRootType.MappedProperty,
    ObjectModelPropertyRootType.RelationshipCount,
    ObjectModelPropertyRootType.DerivedField,
    ObjectModelPropertyRootType.AutoIncrement,
    ObjectModelPropertyRootType.Aggregate,
  ];

  static EXTERNAL_IDABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Text,
    ObjectModelPropertyRootType.PickList,
    ObjectModelPropertyRootType.Number,
    ObjectModelPropertyRootType.DateTime,
    ObjectModelPropertyRootType.MappedProperty,
    ObjectModelPropertyRootType.DerivedField,
    ObjectModelPropertyRootType.AutoIncrement,
  ]

  static DATE_MIN_MAXABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.DateTime,
  ];

  static LINK_TO_RELATIONABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.MappedProperty,
    ObjectModelPropertyRootType.RelationshipCount,
    ObjectModelPropertyRootType.Aggregate,
  ];

  static LINK_TO_RELATION_PROPERTYABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.MappedProperty,
    ObjectModelPropertyRootType.Aggregate,
  ];

  static LINK_TO_RELATION_PROPERTY_FILTERABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Aggregate,
  ];

  static AUTO_INCREMENTABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.AutoIncrement
  ];

  static DERIVED_VALUABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.DerivedField,
  ];

  // Controls whether or not at item can be selected in the object model builder -> properties tab workarea
  static SELECTABLE_CONTROLS: ObjectModelPropertyType[] = EnumHelper.toArray(ObjectModelPropertyRootType)

  static SF_SELECTABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Text,
    // ObjectModelPropertyRootType.LongText, // want this but salesforce does not allow this in push topics yet
    ObjectModelPropertyRootType.PickList,
    ObjectModelPropertyRootType.Number,
    ObjectModelPropertyRootType.DateTime,
    ObjectModelPropertyRootType.MappedProperty, // can be used as lookup field
    ObjectModelPropertyRootType.RelationshipCount, // can be used as lookup field
    ObjectModelPropertyRootType.DerivedField, // can be used as lookup field
    ObjectModelPropertyRootType.AutoIncrement, // can be used as lookup field
  ];

  static READONLY_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.MappedProperty,
    ObjectModelPropertyRootType.RelationshipCount,
    ObjectModelPropertyRootType.DerivedField,
    ObjectModelPropertyRootType.AutoIncrement,
  ];

  static LOGGABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Text,
    ObjectModelPropertyRootType.LongText,
    ObjectModelPropertyRootType.PickList,
    ObjectModelPropertyRootType.Number,
    ObjectModelPropertyRootType.DateTime,
    ObjectModelPropertyRootType.MappedProperty,
    ObjectModelPropertyRootType.RelationshipCount,
    ObjectModelPropertyRootType.DerivedField,
    ObjectModelPropertyRootType.AutoIncrement,
    ObjectModelPropertyRootType.Aggregate,
  ];

  static FILE_ATTACHABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.FileAttachment
  ]

  static REFERENCEABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Text,
    ObjectModelPropertyRootType.LongText,
    ObjectModelPropertyRootType.PickList,
    ObjectModelPropertyRootType.Number,
    ObjectModelPropertyRootType.DateTime,
    ObjectModelPropertyRootType.MappedProperty,
    ObjectModelPropertyRootType.RelationshipCount,
    ObjectModelPropertyRootType.DerivedField,
    ObjectModelPropertyRootType.AutoIncrement,
    ObjectModelPropertyRootType.FileAttachment,
    ObjectModelPropertyRootType.Aggregate,
  ]

  static AGGREGATABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Aggregate,
  ]

  static SECURITY_MASKABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Text,
    ObjectModelPropertyRootType.Number,
    ObjectModelPropertyRootType.DerivedField,
  ]

  static PERMISSION_EDITABLE_CONTROLS: ObjectModelPropertyType[] = [
    ObjectModelPropertyRootType.Text,
    ObjectModelPropertyRootType.LongText,
    ObjectModelPropertyRootType.PickList,
    ObjectModelPropertyRootType.Number,
    ObjectModelPropertyRootType.DateTime,
    ObjectModelPropertyRootType.FileAttachment,
  ]

  static can(type: ObjectModelPropertyType, duty: ObjectModelPropertyCapability) {
    let can: boolean = false;
    switch (duty) {
      case ObjectModelPropertyCapability.labelable:
        can = ObjectModelPropertyCapabilityManager.LABELABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.requireable:
        can = ObjectModelPropertyCapabilityManager.REQUIREABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.formattable:
        can = ObjectModelPropertyCapabilityManager.FORMATTABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.numericFormattable:
        can = ObjectModelPropertyCapabilityManager.NUMBER_FORMATTABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.numericMinMaxable:
        can = ObjectModelPropertyCapabilityManager.NUMBER_FORMATTABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.lengthable:
        can = ObjectModelPropertyCapabilityManager.LENGTHABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.indexable:
        can = ObjectModelPropertyCapabilityManager.INDEXABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.externalIdable:
        can = ObjectModelPropertyCapabilityManager.EXTERNAL_IDABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.dateMinMaxable:
        can = ObjectModelPropertyCapabilityManager.DATE_MIN_MAXABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.linkToRelationable:
        can = ObjectModelPropertyCapabilityManager.LINK_TO_RELATIONABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.linkToRelationPropertyable:
        can = ObjectModelPropertyCapabilityManager.LINK_TO_RELATION_PROPERTYABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.autoIncrementable:
        can = ObjectModelPropertyCapabilityManager.AUTO_INCREMENTABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.derivedValuable:
        can = ObjectModelPropertyCapabilityManager.DERIVED_VALUABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.selectable:
        can = ObjectModelPropertyCapabilityManager.SELECTABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.optionRangeRequireable:
        can = ObjectModelPropertyCapabilityManager.OPTION_RANGE_REQUIREABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.sfSelectable:
        can = ObjectModelPropertyCapabilityManager.SF_SELECTABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.sfUpdatable:
        can = !ObjectModelPropertyCapabilityManager.READONLY_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.readonly:
        can = ObjectModelPropertyCapabilityManager.READONLY_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.loggable:
        can = ObjectModelPropertyCapabilityManager.LOGGABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.fileAttachable:
        can = ObjectModelPropertyCapabilityManager.FILE_ATTACHABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.referenceable:
        can = ObjectModelPropertyCapabilityManager.REFERENCEABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.aggregatable:
        can = ObjectModelPropertyCapabilityManager.AGGREGATABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.securityMaskable:
        can = ObjectModelPropertyCapabilityManager.SECURITY_MASKABLE_CONTROLS.includes(type);
        break;
      case ObjectModelPropertyCapability.permissionEditable:
        can = ObjectModelPropertyCapabilityManager.PERMISSION_EDITABLE_CONTROLS.includes(type);
        break;
      default:
        assertNever(duty);
        can = false;
        break;
    }
    ObjectModelPropertyCapabilityManager.log(`Type ${ObjectModelPropertyRootType[type] || ObjectModelPropertyChildType[type]} ${can ? 'can' : 'can\'t'} ${ObjectModelPropertyCapability[duty]}`);
    return can;
  }

  static addDefaultProperties(f: ObjectModelProperty) {
    // done this way so a case statement can use assertNever to ensure all capabilities are accounted for
    EnumHelper.toArray(ObjectModelPropertyCapability).forEach((c: ObjectModelPropertyCapability) => {
      switch (c) {
        case ObjectModelPropertyCapability.labelable:
          f.defineProperty('label_title', null);
          break;
        case ObjectModelPropertyCapability.requireable:
          f.defineProperty('is_required', false);
          f.defineProperty('required_message', 'Please enter a value');
          break;
        case ObjectModelPropertyCapability.formattable:
          let formatType: InputFormatType;
          if (f.can(ObjectModelPropertyCapability.numericMinMaxable)) {
            formatType = InputFormatType.Numeric;
          } else {
            formatType = InputFormatType.None;
          }
          f.defineProperty('format_type', formatType);
          f.defineProperty('format_message', '');
          f.defineProperty('format_pattern', '');
          break;
        case ObjectModelPropertyCapability.numericFormattable:
          f.defineProperty('decimal_places', null);
          f.defineProperty('currency_type', CurrencyType.USD);
          break;
        case ObjectModelPropertyCapability.numericMinMaxable:
          f.defineProperty('min_value', null);
          f.defineProperty('max_value', null);
          break;
        case ObjectModelPropertyCapability.lengthable:
          f.defineProperty('character_count', CharacterCountFactory.create(f));
          break;
        case ObjectModelPropertyCapability.dateMinMaxable:
          f.defineProperty('datetime_mode', DatetimeMode.Date);
          f.defineProperty('min_datetime', null);
          f.defineProperty('min_datetime_type', DatetimeType.None);
          f.defineProperty('max_datetime', null);
          f.defineProperty('max_datetime_type', DatetimeType.None);
          f.defineProperty('time_format', TimeFormat.TwelveHour);
          break;
        case ObjectModelPropertyCapability.linkToRelationable:
          f.defineProperty('linked_relation_client_id', "");
          break;
        case ObjectModelPropertyCapability.linkToRelationPropertyable:
          f.defineProperty('linked_relation_mapped_properties', []);
          break;
        case ObjectModelPropertyCapability.autoIncrementable:
          f.defineProperty('increment_start', 1);
          f.defineProperty('increment_reset_type', IncrementalResetType.DoNotReset);
          break;
        case ObjectModelPropertyCapability.derivedValuable:
          f.defineProperty('derived_value', null);
          f.defineProperty('derived_value_formula', null);
          break;
        case ObjectModelPropertyCapability.optionRangeRequireable:
          f.defineProperty('selection_type', PickListSelectionType.SingleSelect);
          f.defineProperty('option_range_type', OptionRangeType.AtLeast);
          f.defineProperty('checkbox_range_min', 1);
          f.defineProperty('checkbox_range_max', null);
          break;
        case ObjectModelPropertyCapability.fileAttachable:
          f.defineProperty('file_attachment_type', FileAttachmentType.MultipleFiles);
          break;
        case ObjectModelPropertyCapability.indexable:// field kept on object model ObjectModel#indexed_property_client_ids
        case ObjectModelPropertyCapability.externalIdable:
        case ObjectModelPropertyCapability.selectable:
        case ObjectModelPropertyCapability.sfSelectable:
        case ObjectModelPropertyCapability.sfUpdatable:
        case ObjectModelPropertyCapability.readonly:
          // Do nothing;
          break;
        case ObjectModelPropertyCapability.loggable:
          f.defineProperty('is_logged', false);
          break;
        case ObjectModelPropertyCapability.referenceable:
          f.defineProperty('reference_name', null);
          break;
        case ObjectModelPropertyCapability.aggregatable:
          f.defineProperty('aggregate_type', AggregateType.Sum);
          f.defineProperty('linked_property_has_null_value', true);
          f.defineProperty('null_value', AggregateNullValueType.SubstituteWithZero);
          f.defineProperty('null_replacement_value', 0);
          break;
        case ObjectModelPropertyCapability.securityMaskable:
          f.defineProperty('security_maskable_enabled', false);
          f.defineProperty('security_maskable_partial_display_character_count', 0)
          // concern also includes embedded collection elements; see: parent -> EMBEDDED_MODELS
          break;
        case ObjectModelPropertyCapability.permissionEditable:
          break;
        default:
          assertNever(c);
          break;
      }
    });
  }

  private static log(message: string) {
    if (this.debug) {
      console.log(`DEBUG: ObjectModelPropertyCan: ${message}`);
    }
  }
}